import CircleWarningIcon from '@assets/icons/circle-warning.svg';
import i18n from '@/utils/i18n';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  applyEditToRow,
  calculateWeightedAverages,
  useVariantPackCostChangeRowTransformer,
  VariantPackCostChange,
} from '@/experiences/sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';
import { applyPricingFilters } from '@sales-portal/domain/pricing/blocs/utils/pricing-filters';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import { buildAccountKey } from '@sales-portal/domain/pricing/blocs/utils/account-key';
import { buildPricingFilterSections } from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-sections';
import { Button } from '@usepepper/sprout-kit-ui';
import {
  CATALOG_SECTION_COLUMN_ID,
  CatalogDividerRow,
  CatalogSkeletonRow,
  getCatalogRowKind,
  getCatalogSectionRank,
  makeCatalogDividerRow,
  makeCatalogSkeletonRow,
  tagCatalogResults,
} from '@sales-portal/domain/pricing/screens/management/components/catalog-combined-rows';
import { CatalogSearchEmptyState } from '@/experiences/sales-portal/components/catalog-search-empty-state';
import {
  CatalogSearchInput,
  CatalogSearchInputRef,
} from '@/experiences/sales-portal/components/catalog-search-input';
import {
  COLUMN_WIDTH,
  PRICING_CONTENT_MIN_WIDTH,
  PRICING_FILTER_PANEL_WIDTH,
  PRICING_TABLE_LAYOUT,
} from '@sales-portal/domain/pricing/constants';
import {
  ColumnDef,
  createColumnHelper,
  FilterFn,
  flexRender,
  OnChangeFn,
  Row,
  SortingState,
} from '@tanstack/react-table';
import { ColumnId } from '@/experiences/sales-portal/domain/pricing/screens/management/types';
import {
  ConfigurationDomain,
  TableName,
} from '@/domain/configuration/blocs/stores/column-preferences-keys';
import { ConfigureColumnsButton } from '@experiences/components/table/configure-columns-button';
import { createStyles } from '@/contexts/theme-context';
import { deriveCategoryOptions } from '@sales-portal/domain/pricing/screens/management/blocs/utils/category-filter';
import { EmptyState } from '@experiences/components/empty-state';
import {
  formatCurrency,
  getCurrencySymbol,
  MICRO_CONVERSION,
  roundMicrosToNearestWholeCent,
} from '@/utils/money';
import { formatPercentage } from '@sales-portal/blocs/utils/money';
import {
  getHeaderLabel,
  PricingTableHeader,
} from '@sales-portal/domain/pricing/screens/management/components/pricing-table-header';
import {
  getLastCostInMicros,
  getLastPriceInMicros,
  percentOfPrevious,
  roundPercentageToNearestTenth,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/variant-pack-cost-change-pricing';
import {
  IMPACT_COLUMN_CONFIGS,
  isLeftAlignedColumn,
  resolveDelta,
  variantPackGlobalFilterFn,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/table-ui';
import { LayoutChangeEvent, View } from 'react-native';
import {
  PricingAnalyticEvent,
  pricingManagementAnalytics,
} from '@/experiences/sales-portal/domain/pricing/analytics/pricing-management-analytics';
import { PricingBreadcrumbs } from '@sales-portal/domain/pricing/components/pricing-breadcrumbs';
import {
  PricingCatalogSearchMode,
  selectIsCatalogLoading,
  selectIsCatalogMode,
  usePricingCatalogSearchStore,
} from '@sales-portal/domain/pricing/screens/management/blocs/stores/pricing-catalog-search-store';
import { PricingEditsBadge } from '@sales-portal/domain/pricing/components/pricing-edits-badge';
import { PricingFilterButton } from '@sales-portal/domain/pricing/components/pricing-filter-button';
import { PricingFilterEmptyState } from '@sales-portal/domain/pricing/components/pricing-filter-empty-state';
import { PricingFilterPanel } from '@sales-portal/domain/pricing/components/pricing-filter-panel';
import {
  PricingFilterScope,
  PricingFilterUnit,
  PricingRangeColumnConfig,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { PricingReviewButton } from '@sales-portal/domain/pricing/screens/management/components/pricing-review-button';
import { PricingSearchBar } from '@sales-portal/domain/pricing/components/pricing-search-bar';
import { PricingSummaryHeader } from '@sales-portal/domain/pricing/screens/management/components/pricing-summary-header';
import { PricingTableDeltaCell } from '@/experiences/sales-portal/domain/pricing/screens/management/components/pricing-table-delta-cell';
import { PricingTableEmptyComponent } from '@/experiences/sales-portal/domain/pricing/screens/management/components/pricing-table-empty-component';
import { PricingTableLoadingSkeleton } from '@sales-portal/domain/pricing/components/pricing-table-loading-skeleton';
import { PricingTableRow } from '@/experiences/sales-portal/domain/pricing/screens/management/components/pricing-table-row';
import {
  RenderHeaderRowCallback,
  RenderRowCallback,
  TableScrollHandle,
} from '@experiences/components/table/types';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import {
  SalesPortalKeyboardScope,
  SalesPortalShortcuts,
} from '@/experiences/sales-portal/platform/shortcuts/sales-portal-shortcuts';
import { SalesPortalStackParams } from '@/navigators/types';
import { StackScreenProps } from '@react-navigation/stack';
import { Table } from '@experiences/components/table/table';
import { TableHeaderCell } from '@experiences/components/table/table-header-cell';
import { TableHeaderRow } from '@experiences/components/table/table-header-row';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useBusinessOrganizationPricingConfig } from '@sales-portal/blocs/hooks/use-business-organization-pricing-config';
import { useConfigureColumns } from '@experiences/components/table/blocs/hooks/use-configure-columns';
import { useDebouncedCallback } from 'use-debounce';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useFocusedPricingEditsStore } from '@sales-portal/domain/pricing/blocs/hooks/use-focused-pricing-edits-store';
import { usePricingCatalogSearchLoader } from '@sales-portal/domain/pricing/screens/management/blocs/loaders/use-pricing-catalog-search-loader';
import { usePricingEditsTimezoneSync } from '@sales-portal/domain/pricing/screens/management/blocs/hooks/use-pricing-edits-timezone-sync';
import { usePricingFilterPanel } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filter-panel';
import { usePricingFilters } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filters';
import { usePricingFiltersUrlSync } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filters-url-sync';
import { usePricingSearch } from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-pricing-search';
import { usePricingTableLoadingState } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-table-loading-state';
import { useScreenScopedKeyboardShortcuts } from '@experiences/platform/shortcuts/use-screen-scoped-keyboard-shortcuts';
import { useTableScrollAnchor } from '@experiences/components/table/blocs/hooks/use-table-scroll-anchor';
import { useVariantPackCostChangeDataLoader } from '@/experiences/sales-portal/domain/pricing/screens/management/blocs/loaders/variant-pack-cost-change-data-loader';

const columnHelper = createColumnHelper<VariantPackCostChange>();

// Debounce for re-running the catalog search while already in catalog mode.
const CATALOG_SEARCH_DEBOUNCE_MS = 500;

// Minimum and maximum search catalog query length (matches the BE rule).
const MIN_CATALOG_SEARCH_LENGTH = 3;
const MAX_CATALOG_SEARCH_LENGTH = 120;

type Props = StackScreenProps<
  SalesPortalStackParams,
  typeof SALES_PORTAL_ROUTES.PricingManagement
>;

export const PricingManagementScreen: React.FC<Props> = (props) => {
  const { navigation, route } = props;
  const { costChangeLookbackMode, costChangeWeekAnchorDay } =
    useBusinessOrganizationPricingConfig();
  const [styles, theme] = useStyles();
  const [tableLayoutWidth, setTableLayoutWidth] = useState<number | null>(null);
  const handleTableLayout = useCallback(
    (event: LayoutChangeEvent) =>
      setTableLayoutWidth(event.nativeEvent.layout.width),
    [],
  );
  const { clearSearch, debouncedSearchText, setSearchText } =
    usePricingSearch();
  const catalogMode = usePricingCatalogSearchStore((s) => s.mode);
  const catalogResults = usePricingCatalogSearchStore((s) => s.results);
  const catalogSearchTerm = usePricingCatalogSearchStore((s) => s.searchTerm);
  const onExitCatalogMode = usePricingCatalogSearchStore(
    (s) => s.onExitCatalogMode,
  );
  const isCatalogMode = usePricingCatalogSearchStore(selectIsCatalogMode);
  const isCatalogLoading = usePricingCatalogSearchStore(selectIsCatalogLoading);
  const { showErrorToast } = useExperienceToasts();
  const onCatalogSearchError = useCallback(
    () => showErrorToast(i18n.t('pricing.catalog.error')),
    [showErrorToast],
  );
  const { cancelCatalogSearch, executeCatalogSearch } =
    usePricingCatalogSearchLoader({
      onError: onCatalogSearchError,
    });
  const [sorting, setSorting] = useState<SortingState>([
    { desc: false, id: ColumnId.WeeklyNetGrossProfitImpact },
  ]);

  const edits = useFocusedPricingEditsStore((state) => state.edits);

  const variantPackEditCounts = useMemo(() => {
    return edits.reduce<Record<string, number>>((acc, edit) => {
      acc[edit.variantPackUUID] = (acc[edit.variantPackUUID] ?? 0) + 1;
      return acc;
    }, {});
  }, [edits]);

  // Hoisted above the data loader: the filter sections need currencyCode and
  // the column order, neither of which depends on the loaded rows.
  const { currencyCode, timezone, timezoneLoading } =
    usePricingEditsTimezoneSync();

  const remainingPercentage = 100 - PRICING_TABLE_LAYOUT.NAME_COLUMN_PERCENTAGE;
  const REGULAR_COLUMN_PERCENTAGE =
    remainingPercentage / (PRICING_TABLE_LAYOUT.REGULAR_COLUMN_COUNT + 2);

  const columns = useMemo<ColumnDef<VariantPackCostChange, string>[]>(
    () => [
      // Name
      columnHelper.accessor((row) => row.variantPackName, {
        cell: (info) => {
          const editCount =
            variantPackEditCounts[info.row.original.variantPackUUID];
          return (
            <View style={styles.nameCellContainer}>
              <Typography variant="paragraph-md-600">
                {info.getValue()}
              </Typography>
              {editCount ? <PricingEditsBadge editsCount={editCount} /> : null}
            </View>
          );
        },
        header: () => <PricingTableHeader columnId={ColumnId.Name} />,
        id: ColumnId.Name,
        meta: {
          alwaysVisible: true,
          initialPercentWidth: PRICING_TABLE_LAYOUT.NAME_COLUMN_PERCENTAGE,
          label: getHeaderLabel(ColumnId.Name),
        },
        minSize: PRICING_TABLE_LAYOUT.NAME_COLUMN_MIN_WIDTH,
        sortingFn: 'alphanumeric',
      }),
      // ID
      columnHelper.accessor((row) => row.variantPackId, {
        cell: (info) => (
          <Typography
            color={theme.sk.color.text.primary}
            variant="paragraph-md"
          >
            {info.getValue()}
          </Typography>
        ),
        header: () => <PricingTableHeader columnId={ColumnId.Id} />,
        id: ColumnId.Id,
        meta: {
          initialPercentWidth: REGULAR_COLUMN_PERCENTAGE,
          label: getHeaderLabel(ColumnId.Id),
        },
        ...COLUMN_WIDTH.M,
        sortingFn: 'alphanumeric',
      }),
      // UOM
      columnHelper.accessor((row) => row.variantPackUOM, {
        cell: (info) => (
          <Typography
            color={theme.sk.color.text.primary}
            textTransform="capitalize"
            variant="paragraph-md"
          >
            {info.getValue()}
          </Typography>
        ),
        header: () => <PricingTableHeader columnId={ColumnId.UOM} />,
        id: ColumnId.UOM,
        meta: {
          initialPercentWidth: REGULAR_COLUMN_PERCENTAGE,
          label: getHeaderLabel(ColumnId.UOM),
        },
        ...COLUMN_WIDTH.M,
        sortingFn: 'alphanumeric',
      }),
      // Category
      columnHelper.accessor(
        (row) => {
          const erpGroup = row.groups.find((group) => group.source === 'ERP');
          return (erpGroup ?? row.groups[0])?.displayName ?? '';
        },
        {
          cell: (info) => (
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-md"
            >
              {info.getValue()}
            </Typography>
          ),
          header: () => <PricingTableHeader columnId={ColumnId.Category} />,
          id: ColumnId.Category,
          meta: {
            initialPercentWidth: REGULAR_COLUMN_PERCENTAGE,
            label: getHeaderLabel(ColumnId.Category),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: 'alphanumeric',
        },
      ),
      // Current GM%
      columnHelper.accessor(
        (row) => formatPercentage(row.grossMarginPercentage, 1, 1),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.CurrentGrossMargin}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.CurrentGrossMargin,
          meta: {
            initialPercentWidth: REGULAR_COLUMN_PERCENTAGE,
            label: getHeaderLabel(ColumnId.CurrentGrossMargin),
          },
          ...COLUMN_WIDTH.XS,
          sortingFn: (rowA, rowB) =>
            rowA.original.grossMarginPercentage -
            rowB.original.grossMarginPercentage,
        },
      ),
      // Gross Margin % delta
      columnHelper.accessor(
        (row) =>
          formatPercentage(
            roundPercentageToNearestTenth(row.grossMarginPercentageDelta),
            1,
            1,
          ),
        {
          cell: ({ row }) => {
            const { direction, formattedValue } = resolveDelta(
              roundPercentageToNearestTenth(
                row.original.grossMarginPercentageDelta,
              ),
              (v) => formatPercentage(v, 1, 1),
            );

            return (
              <PricingTableDeltaCell
                arrowDirection={direction}
                value={formattedValue}
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.GrossMarginPercentageDelta}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.GrossMarginPercentageDelta,
          meta: {
            initialPercentWidth: REGULAR_COLUMN_PERCENTAGE,
            label: getHeaderLabel(ColumnId.GrossMarginPercentageDelta),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) =>
            rowA.original.grossMarginPercentageDelta -
            rowB.original.grossMarginPercentageDelta,
        },
      ),
      // Price
      columnHelper.accessor(
        (row) => formatCurrency(row.priceInMicros, currencyCode),
        {
          cell: (info) => (
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-md"
            >
              {info.getValue()}
            </Typography>
          ),
          header: () => <PricingTableHeader columnId={ColumnId.CurrentPrice} />,
          id: ColumnId.CurrentPrice,
          meta: {
            initialPercentWidth: REGULAR_COLUMN_PERCENTAGE,
            label: getHeaderLabel(ColumnId.CurrentPrice),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) =>
            rowA.original.priceInMicros - rowB.original.priceInMicros,
        },
      ),
      // Price delta
      columnHelper.accessor(
        (row) => formatCurrency(row.priceDeltaInMicros, currencyCode),
        {
          cell: (info) => {
            const { direction, formattedValue } = resolveDelta(
              info.row.original.priceDeltaInMicros,
              (v) => formatCurrency(v, currencyCode),
            );

            return (
              <PricingTableDeltaCell
                arrowDirection={direction}
                value={formattedValue}
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.PriceDelta}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.PriceDelta,
          meta: {
            initialPercentWidth: REGULAR_COLUMN_PERCENTAGE,
            label: getHeaderLabel(ColumnId.PriceDelta),
          },
          ...COLUMN_WIDTH.S,
          sortingFn: (rowA, rowB) =>
            rowA.original.priceDeltaInMicros - rowB.original.priceDeltaInMicros,
        },
      ),
      // Cost delta
      columnHelper.accessor(
        (row) => formatCurrency(row.costDeltaInMicros, currencyCode),
        {
          cell: (info) => {
            const { direction, formattedValue } = resolveDelta(
              info.row.original.costDeltaInMicros,
              (v) => formatCurrency(v, currencyCode),
            );

            return (
              <PricingTableDeltaCell
                arrowDirection={direction}
                hasInvertedColor
                value={formattedValue}
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.CostDelta}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.CostDelta,
          meta: {
            initialPercentWidth: REGULAR_COLUMN_PERCENTAGE,
            label: getHeaderLabel(ColumnId.CostDelta),
          },
          ...COLUMN_WIDTH.S,
          sortingFn: (rowA, rowB) =>
            rowA.original.costDeltaInMicros - rowB.original.costDeltaInMicros,
        },
      ),
      // Gross Profit
      columnHelper.accessor(
        (row) => formatCurrency(row.grossProfitInMicros, currencyCode),
        {
          cell: (info) => (
            <Typography variant="paragraph-md">{info.getValue()}</Typography>
          ),
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.GrossProfit}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.GrossProfit,
          meta: {
            initialPercentWidth: REGULAR_COLUMN_PERCENTAGE,
            label: getHeaderLabel(ColumnId.GrossProfit),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) =>
            rowA.original.grossProfitInMicros -
            rowB.original.grossProfitInMicros,
        },
      ),
      // Avg Vol/wk
      columnHelper.accessor((row) => row.quantity.toFixed(1), {
        cell: (info) => (
          <Typography variant="paragraph-md">{info.getValue()}</Typography>
        ),
        header: () => (
          <PricingTableHeader
            columnId={ColumnId.WeeklyVolume}
            costChangeLookbackMode={costChangeLookbackMode}
            costChangeWeekAnchorDay={costChangeWeekAnchorDay}
            withTooltip
          />
        ),
        id: ColumnId.WeeklyVolume,
        meta: {
          initialPercentWidth: REGULAR_COLUMN_PERCENTAGE,
          label: getHeaderLabel(ColumnId.WeeklyVolume),
        },
        ...COLUMN_WIDTH.XS,
        sortingFn: (rowA, rowB) =>
          rowA.original.quantity - rowB.original.quantity,
      }),
      // CC / Edits / Net GP Impact — three explicit impact columns.
      ...IMPACT_COLUMN_CONFIGS.map(({ columnId, getImpact }) =>
        columnHelper.accessor(
          (row) =>
            formatCurrency(
              roundMicrosToNearestWholeCent(getImpact(row)),
              currencyCode,
            ),
          {
            cell: (info) => {
              const { direction, formattedValue } = resolveDelta(
                roundMicrosToNearestWholeCent(getImpact(info.row.original)),
                (v) => formatCurrency(v, currencyCode),
              );

              return (
                <PricingTableDeltaCell
                  arrowDirection={direction}
                  value={formattedValue}
                />
              );
            },
            header: () => (
              <PricingTableHeader columnId={columnId} withTooltip />
            ),
            id: columnId,
            meta: {
              initialPercentWidth: REGULAR_COLUMN_PERCENTAGE,
              label: getHeaderLabel(columnId),
            },
            ...COLUMN_WIDTH.M,
            sortingFn: (rowA, rowB) =>
              getImpact(rowA.original) - getImpact(rowB.original),
          },
        ),
      ),
      // Hidden column that keeps catalog rows sorted below local rows as a
      // section. Never rendered (forced invisible); used only for sorting.
      columnHelper.accessor(getCatalogSectionRank, {
        enableSorting: true,
        header: () => null,
        id: CATALOG_SECTION_COLUMN_ID,
        meta: { hideable: false },
        size: 0,
      }),
    ],
    [
      REGULAR_COLUMN_PERCENTAGE,
      costChangeLookbackMode,
      costChangeWeekAnchorDay,
      currencyCode,
      styles.nameCellContainer,
      theme.sk.color.text.primary,
      variantPackEditCounts,
    ],
  );

  const { columnOrder, columnVisibility } = useConfigureColumns(
    ConfigurationDomain.Pricing,
    TableName.PricingManagement,
    columns,
  );

  // The section-sort column must never render.
  const effectiveColumnVisibility = useMemo(
    () => ({ ...columnVisibility, [CATALOG_SECTION_COLUMN_ID]: false }),
    [columnVisibility],
  );

  const { data, loading, error, refetch } =
    useVariantPackCostChangeDataLoader();

  const perRestaurantRows = useVariantPackCostChangeRowTransformer(
    data?.getVariantPackCostChangeDataForBusinessOrganization ?? [],
  );

  const mergedRows = useMemo(() => {
    if (edits.length === 0) {
      return perRestaurantRows;
    }

    const editsMap = new Map(
      edits.map((edit) => [buildAccountKey(edit), edit]),
    );

    return perRestaurantRows.map((row) => {
      const edit = editsMap.get(buildAccountKey(row));
      return edit ? applyEditToRow(row, edit) : row;
    });
  }, [perRestaurantRows, edits]);

  const aggregatedData = useMemo(
    () => calculateWeightedAverages(mergedRows),
    [mergedRows],
  );

  const categoryOptions = useMemo(
    () => deriveCategoryOptions(aggregatedData),
    [aggregatedData],
  );

  const rangeColumnConfigs = useMemo<
    Record<string, PricingRangeColumnConfig<VariantPackCostChange>>
  >(() => {
    const currencyAffix = getCurrencySymbol(currencyCode);

    const impactRangeEntries: [
      string,
      PricingRangeColumnConfig<VariantPackCostChange>,
    ][] = IMPACT_COLUMN_CONFIGS.map(({ columnId, getImpact }) => [
      columnId,
      {
        accessor: (row) => getImpact(row) / MICRO_CONVERSION,
        affix: currencyAffix,
        title: getHeaderLabel(columnId),
        unit: PricingFilterUnit.Currency,
      },
    ]);

    return {
      [ColumnId.CurrentGrossMargin]: {
        accessor: (row) => row.grossMarginPercentage,
        affix: '%',
        title: getHeaderLabel(ColumnId.CurrentGrossMargin),
        unit: PricingFilterUnit.Percent,
      },
      [ColumnId.GrossMarginPercentageDelta]: {
        accessor: (row) => row.grossMarginPercentageDelta,
        affix: '%',
        title: getHeaderLabel(ColumnId.GrossMarginPercentageDelta),
        unit: PricingFilterUnit.Percent,
      },
      [ColumnId.CurrentPrice]: {
        accessor: (row) => row.priceInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        title: getHeaderLabel(ColumnId.CurrentPrice),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.PriceDelta]: {
        accessor: (row) => row.priceDeltaInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        filterPillMetric: (row) =>
          percentOfPrevious(row.priceDeltaInMicros, getLastPriceInMicros(row)),
        title: getHeaderLabel(ColumnId.PriceDelta),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.CostDelta]: {
        accessor: (row) => row.costDeltaInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        filterPillMetric: (row) =>
          percentOfPrevious(row.costDeltaInMicros, getLastCostInMicros(row)),
        title: getHeaderLabel(ColumnId.CostDelta),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.GrossProfit]: {
        accessor: (row) => row.grossProfitInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        title: getHeaderLabel(ColumnId.GrossProfit),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.WeeklyVolume]: {
        accessor: (row) => row.quantity,
        affix: 'u',
        title: getHeaderLabel(ColumnId.WeeklyVolume),
        unit: PricingFilterUnit.Unit,
      },
      ...Object.fromEntries(impactRangeEntries),
    };
  }, [currencyCode]);

  // Declared order, never `columnOrder` — rearranging the table must not move
  // the filters.
  const filterColumnIds = useMemo(
    () =>
      columns
        .map((column) => column.id)
        .filter((id): id is string => Boolean(id)),
    [columns],
  );

  const sections = useMemo(
    () =>
      buildPricingFilterSections({
        categoryOptions,
        columnIds: filterColumnIds,
        editCounts: {
          counts: variantPackEditCounts,
          getRowId: (row) => row.variantPackUUID,
        },
        rangeColumnConfigs,
        rows: aggregatedData,
        timezone,
      }),
    [
      categoryOptions,
      filterColumnIds,
      variantPackEditCounts,
      rangeColumnConfigs,
      aggregatedData,
      timezone,
    ],
  );

  const filters = usePricingFilters(PricingFilterScope.CostChanges, sections);
  const panel = usePricingFilterPanel();

  const handleFiltersUrlUpdate = useCallback(
    (params: Record<string, string | undefined>) =>
      navigation.setParams(params),
    [navigation],
  );

  usePricingFiltersUrlSync({
    hydrate: filters.hydrate,
    onUrlUpdate: handleFiltersUrlUpdate,
    routeParams: route.params ?? {},
    sections,
    selections: { pills: filters.pills, ranges: filters.ranges },
  });

  // Drop filters whose column is gone, so they can't silently keep shrinking
  // the table. Depends on the store action, not `filters`, which would loop.
  useEffect(() => {
    filters.pruneSections(sections.map((section) => section.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, filters.pruneSections]);

  // Store slices, not `filters`: that object is new every render, so the Table
  // would get a new array on every keystroke.
  const { pills: filterPills, ranges: filterRanges } = filters;
  const transformedData = useMemo(
    () =>
      applyPricingFilters(
        aggregatedData,
        { pills: filterPills, ranges: filterRanges },
        sections,
      ),
    [aggregatedData, filterPills, filterRanges, sections],
  );

  // Excluded from catalog search. Taken pre-filter, so a local pack hidden by
  // an active filter doesn't reappear in the catalog section.
  const existingVariantPackUUIDs = useMemo(
    () => new Set(aggregatedData.map((row) => row.variantPackUUID)),
    [aggregatedData],
  );

  // Errors surface as a toast, with no catalog section.
  const showCatalogSection =
    isCatalogMode && catalogMode !== PricingCatalogSearchMode.CatalogError;

  // Local rows, a "N results in catalog" divider and the catalog rows, all in
  // one virtualized list.
  const combinedData = useMemo(() => {
    if (!showCatalogSection) {
      return transformedData;
    }
    const rows = [
      ...transformedData,
      makeCatalogDividerRow(catalogResults.length),
    ];
    if (catalogMode === PricingCatalogSearchMode.CatalogSearching) {
      rows.push(makeCatalogSkeletonRow());
    } else if (catalogMode !== PricingCatalogSearchMode.CatalogEmpty) {
      // No result rows: the message renders via listFooter so it stays
      // viewport-centered and doesn't scroll sideways.
      rows.push(...tagCatalogResults(catalogResults));
    }
    return rows;
  }, [showCatalogSection, catalogMode, transformedData, catalogResults]);

  // Catalog rows are server-filtered; only local rows filter by search term.
  const combinedGlobalFilterFn = useCallback<FilterFn<VariantPackCostChange>>(
    (row, columnId, filterValue, addMeta) => {
      if (getCatalogRowKind(row.original)) {
        return true;
      }
      return variantPackGlobalFilterFn(row, columnId, filterValue, addMeta);
    },
    [],
  );

  // Hidden section column as primary sort key, so catalog rows stay below
  // local ones whatever the user sorts by.
  const effectiveSorting = useMemo<SortingState>(
    () =>
      showCatalogSection
        ? [{ desc: false, id: CATALOG_SECTION_COLUMN_ID }, ...sorting]
        : sorting,
    [showCatalogSection, sorting],
  );

  const { restaurants, totalCostChangeImpact, totalEditsImpact } =
    useMemo(() => {
      return transformedData.reduce(
        (acc, row) => {
          acc.restaurants.add(row.restaurantUUID);
          acc.totalCostChangeImpact += row.costChangeGrossProfitImpactInMicros;
          acc.totalEditsImpact += row.editsGrossProfitImpactInMicros;
          return acc;
        },
        {
          restaurants: new Set<string>(),
          totalCostChangeImpact: 0,
          totalEditsImpact: 0,
        },
      );
    }, [transformedData]);

  const searchInputRef = useRef<CatalogSearchInputRef>(null);
  const scrollHandleRef = useRef<TableScrollHandle | null>(null);
  const getPricingManagementRowKey = useCallback(
    (row: Row<VariantPackCostChange>) => row.original.variantPackUUID,
    [],
  );

  const {
    initialScrollIndex,
    onRowsChange,
    recordAnchorForRow,
    recordCurrentTopmostRow,
  } = useTableScrollAnchor({
    getRowKey: getPricingManagementRowKey,
    isReady: transformedData.length > 0,
    scope: 'pricing-management',
    scrollHandleRef,
  });

  // Reset on leave so a stale search doesn't linger on return.
  useEffect(() => () => onExitCatalogMode(), [onExitCatalogMode]);

  const hasTrackedLoad = useRef(false);
  useEffect(() => {
    if (!loading && !error && data && !hasTrackedLoad.current) {
      hasTrackedLoad.current = true;
      pricingManagementAnalytics.track(
        PricingAnalyticEvent.LOAD_COST_CHANGES_ITEM_LIST,
        {},
      );
    }
  }, [loading, error, data]);

  // Keyboard shortcut: / to focus search input
  useScreenScopedKeyboardShortcuts(
    SalesPortalShortcuts.Search,
    () => searchInputRef.current?.focus(),
    [],
    {},
    true,
    SalesPortalKeyboardScope.PRICING_MANAGEMENT,
  );

  const { alertMe, loadingState, onAlertMePress } = usePricingTableLoadingState(
    {
      error,
      loading: loading || timezoneLoading,
    },
  );

  const debouncedCatalogSearch = useDebouncedCallback((text: string) => {
    executeCatalogSearch(text.trim(), existingVariantPackUUIDs);
  }, CATALOG_SEARCH_DEBOUNCE_MS);

  // Exit catalog mode back to local filtering, cancelling any pending search.
  const exitCatalogMode = useCallback(() => {
    debouncedCatalogSearch.cancel();
    cancelCatalogSearch();
    onExitCatalogMode();
  }, [cancelCatalogSearch, debouncedCatalogSearch, onExitCatalogMode]);

  const handleSearchChange = useCallback(
    (text: string) => {
      // Keep the local list filtered live regardless of mode.
      setSearchText(text);
      if (!isCatalogMode) {
        return;
      }
      // Refine as the user types, or drop back to local filtering below the
      // minimum searchable length.
      if (text.trim().length >= MIN_CATALOG_SEARCH_LENGTH) {
        debouncedCatalogSearch(text);
      } else {
        exitCatalogMode();
      }
    },
    [debouncedCatalogSearch, exitCatalogMode, isCatalogMode, setSearchText],
  );

  // Enter / "Search catalog" pill: run the catalog search immediately.
  const handleCatalogSearch = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      // Match the BE rule: don't hit the API below the minimum query length.
      if (trimmed.length < MIN_CATALOG_SEARCH_LENGTH) {
        return;
      }
      debouncedCatalogSearch.cancel();
      executeCatalogSearch(trimmed, existingVariantPackUUIDs);
    },
    [debouncedCatalogSearch, executeCatalogSearch, existingVariantPackUUIDs],
  );

  // Clear (X) / Escape: reset the input text and local filter, exit catalog mode.
  const handleClearSearchInput = useCallback(() => {
    searchInputRef.current?.clear();
    clearSearch();
    exitCatalogMode();
  }, [clearSearch, exitCatalogMode]);

  const onSortingChange = useCallback<OnChangeFn<SortingState>>(
    (sorting) => setSorting(sorting),
    [setSorting],
  );

  const loadingSkeleton = (
    <PricingTableLoadingSkeleton
      alertMe={alertMe}
      loadingState={loadingState}
      onAlertMePress={onAlertMePress}
      phraseKeyPrefix="pricing.cost_changes.table.loading"
      testID="pricing-management-loading-skeleton"
    />
  );

  const renderHeaderRow = useCallback<
    RenderHeaderRowCallback<VariantPackCostChange>
  >(
    ({ headerGroup }) => (
      <TableHeaderRow key={headerGroup.id}>
        {headerGroup.headers.map((header) => {
          return (
            <TableHeaderCell
              columnId={header.id}
              key={header.id}
              onPress={
                header.column.getCanSort()
                  ? () => header.column.toggleSorting()
                  : undefined
              }
              pinnedOffset={
                header.column.getIsPinned()
                  ? header.column.getStart('left')
                  : undefined
              }
              sortDirection={header.column.getIsSorted() || undefined}
              style={
                isLeftAlignedColumn(header.column.id as ColumnId)
                  ? undefined
                  : styles.headerCell
              }
            >
              {header.isPlaceholder ? null : (
                <View style={styles.columnHeaderContainer}>
                  <Typography
                    color={theme.sk.color.text.tertiary}
                    textAlign={
                      isLeftAlignedColumn(header.column.id as ColumnId)
                        ? undefined
                        : 'right'
                    }
                    variant="paragraph-sm-600"
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </Typography>
                </View>
              )}
            </TableHeaderCell>
          );
        })}
      </TableHeaderRow>
    ),
    [styles, theme],
  );

  const renderRow = useCallback<RenderRowCallback<VariantPackCostChange>>(
    ({ index, row }) => {
      const kind = getCatalogRowKind(row.original);
      if (kind === 'divider') {
        const label =
          catalogMode === PricingCatalogSearchMode.CatalogSearching
            ? i18n.t('pricing.catalog.searching')
            : i18n.t('pricing.catalog.results_header', {
                count: catalogResults.length,
              });
        return <CatalogDividerRow index={index} key={row.id} label={label} />;
      }
      if (kind === 'skeleton') {
        return <CatalogSkeletonRow index={index} key={row.id} />;
      }
      return (
        <PricingTableRow
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          index={index}
          key={row.id}
          onBeforeNavigate={() => recordAnchorForRow(row, index)}
          row={row}
        />
      );
    },
    [
      catalogMode,
      catalogResults.length,
      columnOrder,
      columnVisibility,
      recordAnchorForRow,
    ],
  );

  const handleClearEmptyState = useCallback(() => {
    if (debouncedSearchText.length > 0) {
      handleClearSearchInput();
    } else {
      filters.clearAll();
    }
  }, [debouncedSearchText, handleClearSearchInput, filters]);

  return (
    <SalesHubShellTemplate>
      <AppPageLayout
        actions={
          edits.length > 0 ? (
            <View
              style={panel.isOpen ? styles.actionsWithPanelOpen : undefined}
            >
              <PricingReviewButton
                onPress={() => {
                  recordCurrentTopmostRow();
                  navigation.navigate(
                    SALES_PORTAL_ROUTES.PricingManagementReview,
                  );
                }}
              />
            </View>
          ) : undefined
        }
        leadingActions={
          <PricingBreadcrumbs
            secondaryBreadcrumb={i18n.t('pricing.cost_changes.sidenav_title')}
          />
        }
        overlay={
          panel.isOpen ? (
            <PricingFilterPanel
              filters={filters}
              onClose={panel.close}
              sections={sections}
            />
          ) : null
        }
        scroll={false}
      >
        <View
          style={[
            styles.container,
            panel.isOpen && styles.containerWithPanelOpen,
          ]}
        >
          {loading || timezoneLoading ? (
            loadingSkeleton
          ) : error ? (
            <EmptyState
              bottomElement={
                <Button
                  accessibilityLabel={i18n.t('sales_portal.error.retry')}
                  onPress={async () => await refetch()}
                  style={styles.autoAlignSelf}
                  variant="secondary"
                >
                  {i18n.t('sales_portal.error.retry')}
                </Button>
              }
              description={i18n.t('pricing.error.table.description')}
              iconElement={
                <CircleWarningIcon stroke={theme.sk.color.icon.primary} />
              }
              title={i18n.t('pricing.error.table.title')}
            />
          ) : (
            <Suspense fallback={loadingSkeleton}>
              <PricingSearchBar
                trailing={
                  <>
                    <PricingFilterButton
                      activeCount={filters.activeCount}
                      onPress={panel.open}
                    />
                    <ConfigureColumnsButton
                      columns={columns}
                      domain={ConfigurationDomain.Pricing}
                      table={TableName.PricingManagement}
                    />
                  </>
                }
              >
                <CatalogSearchInput
                  isInCatalogMode={isCatalogMode}
                  isLoading={isCatalogLoading}
                  labels={{
                    catalogModePlaceholder: i18n.t(
                      'pricing.search_catalog_placeholder',
                    ),
                    catalogSearchCta: i18n.t('pricing.search_catalog_cta'),
                    clearSearch: i18n.t('clear_search'),
                    defaultPlaceholder: i18n.t('pricing.search_placeholder'),
                    keyboardHint: i18n.t(
                      'pricing.search_catalog_keyboard_hint',
                    ),
                  }}
                  maxLength={MAX_CATALOG_SEARCH_LENGTH}
                  minCatalogSearchLength={MIN_CATALOG_SEARCH_LENGTH}
                  onCatalogSearch={handleCatalogSearch}
                  onClearAndExit={handleClearSearchInput}
                  onEscapePress={handleClearSearchInput}
                  onSearchChange={handleSearchChange}
                  ref={searchInputRef}
                />
              </PricingSearchBar>
              <PricingSummaryHeader
                costChangeImpact={totalCostChangeImpact}
                currencyCode={currencyCode}
                editsImpact={totalEditsImpact}
                restaurantCount={restaurants.size}
                title={i18n.t('pricing.cost_changes.sidenav_title')}
                variantPackCount={transformedData.length}
              />
              <View onLayout={handleTableLayout} style={styles.tableWrapper}>
                <Table
                  cellPadding={[
                    ...Array(columns.length).fill(theme.sk.spacing.spaceSmall),
                  ]}
                  columns={columns}
                  data={combinedData}
                  emptyComponent={
                    <PricingFilterEmptyState
                      baseEmptyComponent={<PricingTableEmptyComponent />}
                      hasActiveFilters={filters.hasActiveFilters}
                      onClear={handleClearEmptyState}
                      searchText={debouncedSearchText}
                    />
                  }
                  estimatedRowHeight={PRICING_TABLE_LAYOUT.ESTIMATED_ROW_HEIGHT}
                  fillAvailableWidth
                  globalFilterFn={combinedGlobalFilterFn}
                  headerPadding={theme.sk.spacing.spaceSmall}
                  initialScrollIndex={initialScrollIndex}
                  initialState={{
                    columnPinning: {
                      left: [ColumnId.Name],
                    },
                  }}
                  isActive
                  layoutWidth={tableLayoutWidth ?? undefined}
                  listFooter={
                    catalogMode === PricingCatalogSearchMode.CatalogEmpty ? (
                      <View style={styles.catalogEmptyFooter}>
                        <CatalogSearchEmptyState
                          actionLabel={i18n.t(
                            'pricing.catalog.empty.clear_search',
                          )}
                          description={i18n.t(
                            'pricing.catalog.empty.description',
                          )}
                          onAction={handleClearSearchInput}
                          title={i18n.t('pricing.catalog.empty.title', {
                            query: catalogSearchTerm,
                          })}
                        />
                      </View>
                    ) : undefined
                  }
                  onFilteredRowsChange={onRowsChange}
                  onSortingChange={onSortingChange}
                  renderHeaderRow={renderHeaderRow}
                  renderRow={renderRow}
                  scrollHandleRef={scrollHandleRef}
                  state={{
                    columnOrder,
                    columnVisibility: effectiveColumnVisibility,
                    globalFilter: debouncedSearchText,
                    sorting: effectiveSorting,
                  }}
                />
              </View>
            </Suspense>
          )}
        </View>
      </AppPageLayout>
    </SalesHubShellTemplate>
  );
};

const useStyles = createStyles((theme) => ({
  actionsWithPanelOpen: {
    paddingRight: PRICING_FILTER_PANEL_WIDTH,
  },
  autoAlignSelf: {
    alignSelf: 'auto',
  },
  catalogEmptyFooter: {
    marginTop: theme.sk.spacing.spaceXlarge,
  },
  columnHeaderContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    flexShrink: 1,
    minWidth: 0,
  },
  container: {
    flex: 1,
    gap: theme.sk.spacing.spaceMedium,
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  containerWithPanelOpen: {
    minWidth: PRICING_CONTENT_MIN_WIDTH + PRICING_FILTER_PANEL_WIDTH,
    paddingRight: PRICING_FILTER_PANEL_WIDTH,
  },
  headerCell: {
    alignItems: 'flex-end',
  },
  nameCellContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    justifyContent: 'space-between',
  },
  tableWrapper: {
    flex: 1,
    position: 'relative',
  },
}));
